import React, { useState, useEffect } from 'react';
import { 
  Users, 
  UserPlus, 
  UserMinus, 
  Building2, 
  Shield, 
  CheckCircle, 
  Search,
  Filter,
  Star
} from 'lucide-react';
import AssignSiteModal from './AssignSiteModal';
import { userAssignmentsService, UserSiteAssignment, CreateAssignmentRequest } from '../../services/userAssignments';
import userService, { Role } from '../../services/users';
import { PermissionGuard } from '../common/PermissionGuard';
import { PERMISSIONS } from '../../services/permissions';
import './UserAssignment.css';

interface Site {
  id: number;
  code: string;
  name: string;
  site_type: string;
  address: string;
  is_active: boolean;
  tenant: {
    id: number;
    name: string;
    code: string;
  };
}

interface User {
  id: number;
  username: string;
  full_name: string;
  email: string;
  is_active: boolean;
  role?: {
    id: number;
    name: string;
  };
}


const UserAssignment: React.FC<{ sites: Site[] }> = ({ sites }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [userSites, setUserSites] = useState<UserSiteAssignment[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSite, setFilterSite] = useState<string>('');
  



  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  // Récupérer les assignations quand la liste des utilisateurs change
  useEffect(() => {
    if (users.length > 0) {
      fetchUserSites();
    }
  }, [users]);

  const fetchUsers = async () => {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      console.error('❌ Aucun token d\'accès pour récupérer les utilisateurs');
      setUsers([]);
      return;
    }
    
    try {
      let allUsers: any[] = [];
      let nextUrl = 'http://localhost:8000/api/v1/users/';
      
      // Récupérer toutes les pages d'utilisateurs
      while (nextUrl) {
       
        const response = await fetch(nextUrl, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
         
          if (data.results && Array.isArray(data.results)) {
            allUsers.push(...data.results);
          }
          
          nextUrl = data.next; // URL de la page suivante
        } else {
          console.error('❌ Erreur HTTP:', response.status, response.statusText);
          break;
        }
      }
      
     
      setUsers(allUsers);
      
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des utilisateurs:', error);
      setUsers([]);
    }
  };

  const fetchRoles = async () => {
    try {
      const rolesData = await userService.getRoles();
      const rolesArray = Array.isArray(rolesData) ? rolesData : rolesData.results || [];
      setRoles(rolesArray);
     
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des rôles:', error);
      setRoles([]);
    }
  };

  const fetchUserSites = async () => {
    try {
      
      
      // Utiliser la nouvelle méthode unifiée - récupérer toutes les assignations
      // Note: Cette méthode nécessite un userId, nous devons adapter la logique
      const allAssignments: UserSiteAssignment[] = [];
      
      // Récupérer les assignations pour chaque utilisateur
      for (const user of users) {
       
        try {
          const userAssignments = await userAssignmentsService.getAssignments(user.id.toString());
          
          allAssignments.push(...userAssignments);
        } catch (error) {
          console.warn(`⚠️ Impossible de récupérer les assignations pour l'utilisateur ${user.id} (${user.username}):`, error);
        }
      }
      
      
      setUserSites(allAssignments);
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des attributions:', error);
      setUserSites([]);
    }
  };

  const handleAssignUser = async (
    siteId: number, 
    roleId: string, 
    sessionType: 'PERMANENTE' | 'TEMPORAIRE', 
    isDefault: boolean, 
    startDate?: string, 
    endDate?: string
  ) => {
    setIsSubmitting(true);
    
    try {
      if (!selectedUser) {
        throw new Error('Aucun utilisateur sélectionné');
      }
      
      // Utiliser la nouvelle interface et méthode unifiée
      const assignment: CreateAssignmentRequest = {
        user: selectedUser.id,
        site: siteId,
        role: parseInt(roleId),
        session_type: sessionType,
        starts_at: startDate,
        ends_at: endDate,
        is_default: isDefault
      };
      
      await userAssignmentsService.createAssignment(selectedUser.id.toString(), assignment);
      await fetchUserSites();
      alert('✅ Utilisateur attribué avec succès !');
    } catch (error: any) {
      console.error('❌ Erreur lors de l\'attribution:', error);
      alert(`Erreur lors de l'attribution: ${error.message || 'Erreur inconnue'}`);
      throw error; // Re-lancer l'erreur pour que le modal ne se ferme pas
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveUser = async (userSiteId: number) => {
    if (!confirm('Êtes-vous sûr de vouloir retirer cet utilisateur de ce site ?')) return;
    
    try {
      // Utiliser la nouvelle méthode unifiée
      // Nous devons d'abord identifier l'utilisateur pour cette assignation
      const assignment = userSites.find(us => us.id === userSiteId);
      if (!assignment) {
        throw new Error('Assignation non trouvée');
      }
      
      await userAssignmentsService.deleteAssignment(assignment.user.toString(), userSiteId.toString());
      await fetchUserSites();
      alert('✅ Utilisateur retiré avec succès !');
    } catch (error: any) {
      console.error('Erreur lors du retrait de l\'utilisateur:', error);
      alert(`Erreur lors du retrait: ${error.message || 'Erreur inconnue'}`);
    }
  };

  const handleSetDefaultSite = async (userId: number, siteId: number) => {
    if (!confirm('Définir ce site comme site par défaut pour cet utilisateur ?')) return;
    
    try {
      // Utiliser la nouvelle méthode unifiée
      // Nous devons d'abord identifier l'assignation pour cet utilisateur et ce site
      const assignment = userSites.find(us => us.user === userId && us.site === siteId);
      if (!assignment) {
        throw new Error('Assignation non trouvée');
      }
      
      await userAssignmentsService.setDefaultSite(userId.toString(), assignment.id.toString());
      await fetchUserSites();
      alert('✅ Site par défaut défini avec succès !');
    } catch (error: any) {
      console.error('Erreur lors de la définition du site par défaut:', error);
      alert(`Erreur lors de la définition du site par défaut: ${error.message || 'Erreur inconnue'}`);
    }
  };


  const filteredUserSites = userSites.filter(userSite => {
    // Vérifications de sécurité pour éviter les erreurs
    if (!userSite?.user || !userSite?.site) {
     
      return false;
    }
    
    // Utiliser les champs calculés de l'API
    const userFullName = userSite.user_username || '';
    const username = userSite.user_username || '';
    const siteId = userSite.site;
    
    const matchesSearch = userFullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = !filterSite || siteId.toString() === filterSite;
    
    const result = matchesSearch && matchesFilter;
   
    
    return result;
  });



  return (
    <div className="p-6">


      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Users className="w-6 h-6 text-orange-500 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">Attribution des Utilisateurs</h2>
        </div>
        <p className="text-gray-600">
          Gérez les utilisateurs et leurs rôles sur chaque site de votre organisation.
        </p>
      </div>

      {/* Statistiques des attributions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total attributions</p>
              <p className="text-2xl font-semibold text-gray-900">{userSites.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Star className="w-8 h-8 text-orange-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Sites par défaut</p>
              <p className="text-2xl font-semibold text-gray-900">
                {userSites.filter(us => us.is_default).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Building2 className="w-8 h-8 text-green-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Sites actifs</p>
              <p className="text-2xl font-semibold text-gray-900">
                {sites.filter(site => site.is_active).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Shield className="w-8 h-8 text-purple-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Utilisateurs actifs</p>
              <p className="text-2xl font-semibold text-gray-900">
                {users.filter(user => user.is_active).length}
              </p>
            </div>
          </div>
        </div>
      </div>

             {/* Bouton d'ouverture du modal */}
       <div className="user-assignment-section">
         <div className="user-assignment-header">
           <h3 className="text-lg font-medium text-gray-900 flex items-center">
             <UserPlus className="w-5 h-5 text-orange-500 mr-2" />
             Attribuer un utilisateur à un site
           </h3>
         </div>
         
         <div className="user-assignment-content">
           <div className="mb-4">
             <label className="block text-sm font-medium text-gray-700 mb-2">
               Sélectionner un utilisateur
             </label>
             <select
               value={selectedUser?.id || ''}
               onChange={(e) => {
                 const userId = parseInt(e.target.value);
                 const user = users.find(u => u.id === userId);
                 setSelectedUser(user || null);
               }}
               className="custom-select"
             >
               <option value="">Choisir un utilisateur...</option>
               {users.map(user => (
                 <option key={user.id} value={user.id}>
                   {user.full_name} ({user.username}) - {user.email}
                 </option>
               ))}
             </select>
           </div>
           
          <PermissionGuard permission={PERMISSIONS.USER_MANAGEMENT_ASSIGN_ROLES}>
            <button
              onClick={() => setIsModalOpen(true)}
              disabled={!selectedUser}
              className="action-button primary w-full py-3"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Attribuer le site à {selectedUser ? selectedUser.full_name : 'l\'utilisateur'}
            </button>
          </PermissionGuard>
         </div>
       </div>

      {/* Filtres et recherche */}
      <div className="user-assignment-section mb-6">
        <div className="user-assignment-header">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Filter className="w-5 h-5 text-orange-500 mr-2" />
            Filtres et recherche
          </h3>
        </div>
        
        <div className="user-assignment-content">
          <div className="form-grid">
            <div className="form-field-group">
              <label className="form-field-label">
                Rechercher un utilisateur
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom d'utilisateur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="custom-form-field pl-10"
                />
              </div>
            </div>
            
            <div className="form-field-group">
              <label className="form-field-label">
                Filtrer par site
              </label>
              <select
                value={filterSite}
                onChange={(e) => setFilterSite(e.target.value)}
                className="custom-select"
              >
                <option value="">Tous les sites</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des attributions */}
      <div className="user-assignment-section">
        <div className="user-assignment-header">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            Attributions actuelles
          </h3>
        </div>
        
        {filteredUserSites.length === 0 ? (
          <div className="user-assignment-content text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune attribution trouvée</h3>
            <p className="text-gray-600">
              {searchTerm || filterSite 
                ? 'Aucune attribution ne correspond à vos critères de recherche.'
                : 'Aucun utilisateur n\'a encore été attribué à un site.'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {filteredUserSites.map((userSite) => (
                <li key={userSite.id} className="list-item">
                  <div className="list-item-content">
                    <div className="flex items-center">
                      <div className="list-item-avatar">
                        <Users className="h-5 w-5 text-orange-600" />
                      </div>
                      
                      <div className="list-item-info">
                        <div className="flex items-center">
                          <p className="list-item-title">
                            {userSite.user_username || 'Nom non défini'}
                          </p>
                          {userSite.is_default && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              Défaut
                            </span>
                          )}
                        </div>
                        <p className="list-item-subtitle">{userSite.user_email || 'Email non défini'}</p>
                        <div className="list-item-meta">
                          <Building2 className="w-4 h-4 text-gray-400 mr-1" />
                          <span className="text-sm text-gray-600">
                            {userSite.site_name || 'Site non défini'} ({userSite.site_code || 'N/A'})
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="list-item-actions">
                      <span className={`role-badge ${(userSite.role_label || '').toString().toLowerCase()}`}>
                        {userSite.role_label || 'Rôle non défini'}
                      </span>
                      
                      {!userSite.is_default && (
                        <PermissionGuard permission={PERMISSIONS.USER_MANAGEMENT_EDIT}>
                          <button
                            onClick={() => handleSetDefaultSite(userSite.user, userSite.site)}
                            className="action-button secondary mr-2"
                            title="Définir comme site par défaut"
                          >
                            <Star className="w-4 h-4" />
                          </button>
                        </PermissionGuard>
                      )}
                      
                      <PermissionGuard permission={PERMISSIONS.USER_MANAGEMENT_DELETE}>
                        <button
                          onClick={() => handleRemoveUser(userSite.id)}
                          className="action-button danger"
                          title="Retirer l'utilisateur"
                        >
                          <UserMinus className="w-4 h-4" />
                        </button>
                      </PermissionGuard>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Modal d'attribution */}
      {selectedUser && (
        <AssignSiteModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onAssign={handleAssignUser}
          sites={sites}
          roles={roles.map(role => ({
            id: String(role.id),
            name: role.label || role.name,
            description: role.description
          }))}
          userId={selectedUser.id}
          userName={selectedUser.username}
          userEmail={selectedUser.email}
          userFirstName={selectedUser.full_name.split(' ')[0]}
          userLastName={selectedUser.full_name.split(' ').slice(1).join(' ')}
          loading={isSubmitting}
        />
      )}
    </div>
  );
};

export default UserAssignment;
